import { DisplayMode } from '@rta/rta-blueprint-component';
import { FileText } from 'lucide-react';

import { ControllerDisplayModeSupport } from './types';

import { DesignState } from '@context';
import { getDesignImage } from '@helpers';
import { lang } from '@lang';
import { useDesign, useAuthorize } from '@hooks';
import { useSubscription } from '@rta/rta-blueprint-library';
import { Feature, FeatureHoookParams } from '..';

type UseDisplayBlueprintViewModeFeatureParams = FeatureHoookParams;
export const useDisplayBlueprintViewModeFeature = (_params: UseDisplayBlueprintViewModeFeatureParams = {}): Feature<boolean> => {
	const [controller, designState] = useDesign() as [((typeof useDesign extends (...args: unknown[]) => [infer C, ...unknown[]] ? C : never) & ControllerDisplayModeSupport) | null, DesignState | null];

	const canViewBlueprint = useAuthorize('view:blueprint-document');
	const controllerSupportsFeature = (controller?.navigation?.displayMode ?? null) !== null;
	const inBlueprintView = controller?.navigation?.displayMode === DisplayMode.BlueprintView;

	useSubscription(controller, false, [['navigation-display-mode', '*']]);

	const [disabled, tooltip] = (() => {
		if (!canViewBlueprint) return [true, 'Blueprint view requires the view:blueprint-document capability'];
		if (!controllerSupportsFeature) return [true, lang.navigation.tooltip.disabledNotSupportCurrentView];
		return [false, lang.navigation.tooltip.blueprintViewMode];
	})();

	return {
		name: 'blueprint-view-mode',
		label: lang.navigation.label.blueprintViewMode,
		shortcut: '',
		tooltip,
		icon: (props = {}) => <FileText color={disabled ? 'gray' : inBlueprintView ? 'green' : 'black'} {...props} />,
		disabled,
		value: inBlueprintView,
		hidden: false,
		handler: () => {
			if (controllerSupportsFeature) {
				if (inBlueprintView) {
					controller.navigation.displayMode = DisplayMode.Panel;
				} else {
					// Prefer the saved rendering — only fall back to a live 3D capture when none exists
					const savedRendering = getDesignImage(designState?.blueprint);
					const tdController = designState?.tdController;
					const liveImage = !savedRendering && tdController && typeof tdController.getImage === 'function' ? tdController.getImage() : '';
					const image = savedRendering || liveImage;
					if (image && controller.options) {
						controller.options.threeDImageUrl = image;
					}
					controller.navigation.displayMode = DisplayMode.BlueprintView;
				}
			}
		}
	};
};
